// Common Styles - ExportMate Design System
// Use these consistent styles across all components

// Color Palette
export const colors = {
  primary: '#3b82f6',
  primary600: '#2563eb',
  primary100: '#dbeafe',
  primary50: '#eff6ff',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#dc2626',
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray500: '#6b7280',
  gray700: '#374151',
  gray900: '#1f2937',
  white: '#ffffff',
  black: '#000000'
};

// Elevation — layered, cool-tinted shadows (mirror index.css --shadow-*).
export const shadows = {
  xs: '0 1px 2px rgba(16,24,40,0.06)',
  sm: '0 1px 2px rgba(16,24,40,0.06), 0 1px 3px rgba(16,24,40,0.10)',
  md: '0 4px 8px -2px rgba(16,24,40,0.10), 0 2px 4px -2px rgba(16,24,40,0.06)',
  lg: '0 12px 16px -4px rgba(16,24,40,0.08), 0 4px 6px -2px rgba(16,24,40,0.03)'
};

// Radii (mirror index.css --radius-*).
export const radii = { sm: 8, md: 10, lg: 12, xl: 16 };

// Layout
export const card = {
  background: colors.white,
  border: `1px solid ${colors.gray200}`,
  borderRadius: radii.xl,
  padding: 24,
  boxShadow: shadows.sm
};

export const cardSmall = {
  ...card,
  padding: 16
};

// Typography scale (single source of truth — mirror index.css --font-size-*)
export const typography = {
  pageTitle: 28,
  pageSubtitle: 14,
  sectionTitle: 20,
  subsectionTitle: 18,
  label: 13,
  input: 14,
  tableHeader: 13,
  tableBody: 14,
  helper: 12,
  caption: 11
};

// Typography
export const h1 = {
  fontSize: typography.pageTitle,
  fontWeight: 700,
  color: colors.gray900,
  marginBottom: 8
};

export const h2 = {
  fontSize: typography.sectionTitle,
  fontWeight: 600,
  color: colors.gray900,
  marginBottom: 16
};

export const h3 = {
  fontSize: typography.subsectionTitle,
  fontWeight: 600,
  color: colors.gray900,
  marginBottom: 12
};

export const label = {
  display: 'block',
  fontSize: typography.label,
  fontWeight: 600,
  marginBottom: 6,
  color: colors.gray700
};

export const text = {
  fontSize: typography.input,
  color: colors.gray700,
  lineHeight: 1.5
};

export const helperText = {
  fontSize: typography.helper,
  color: colors.gray500,
  lineHeight: 1.45
};

export const caption = {
  fontSize: typography.caption,
  color: colors.gray500,
  lineHeight: 1.4
};

// Form Elements
export const input = {
  width: '100%',
  padding: '10px 12px',
  border: `1px solid ${colors.gray300}`,
  borderRadius: radii.md,
  fontSize: typography.input,
  boxSizing: 'border-box',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  outline: 'none'
};

export const inputError = {
  ...input,
  borderColor: colors.error,
  borderWidth: '2px'
};

export const textarea = {
  ...input,
  minHeight: 90,
  resize: 'vertical'
};

export const select = {
  ...input,
  cursor: 'pointer'
};

// Buttons
export const primaryBtn = {
  padding: '10px 16px',
  borderRadius: radii.md,
  background: colors.primary,
  color: colors.white,
  border: 'none',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
  minHeight: 40,
  boxSizing: 'border-box',
  transition: 'filter 0.15s, transform 0.05s, box-shadow 0.15s',
  boxShadow: shadows.xs
};

export const successBtn = {
  ...primaryBtn,
  background: colors.success
};

export const warningBtn = {
  ...primaryBtn,
  background: colors.warning
};

export const dangerBtn = {
  ...primaryBtn,
  background: colors.error
};

export const ghostBtn = {
  padding: '10px 16px',
  borderRadius: radii.md,
  border: `1px solid ${colors.gray300}`,
  background: colors.white,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 500,
  color: colors.gray700,
  minHeight: 40,
  boxSizing: 'border-box',
  transition: 'filter 0.15s, transform 0.05s, background-color 0.15s, border-color 0.15s'
};

export const iconBtn = {
  background: colors.primary,
  border: 'none',
  color: colors.white,
  cursor: 'pointer',
  padding: 8,
  borderRadius: radii.sm,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'filter 0.15s, transform 0.05s, box-shadow 0.15s',
  boxShadow: shadows.xs
};

export const iconBtnSuccess = {
  ...iconBtn,
  background: colors.success
};

export const iconBtnDanger = {
  ...iconBtn,
  background: colors.error
};

export const linkBtn = {
  background: 'none',
  border: 'none',
  color: colors.primary,
  cursor: 'pointer',
  padding: 0,
  fontSize: 13,
  textDecoration: 'underline',
  fontWeight: 500
};

// Messages
export const errorMessage = {
  marginTop: 8,
  padding: '8px 12px',
  backgroundColor: '#fee2e2',
  color: colors.error,
  borderRadius: 6,
  fontSize: 13,
  border: '1px solid #fecaca'
};

export const successMessage = {
  marginTop: 8,
  padding: '8px 12px',
  backgroundColor: '#dcfce7',
  color: colors.success,
  borderRadius: 6,
  fontSize: 13,
  border: '1px solid #bbf7d0'
};

export const warningMessage = {
  marginTop: 8,
  padding: '8px 12px',
  backgroundColor: '#fef3c7',
  color: '#92400e',
  borderRadius: 6,
  fontSize: 13,
  border: '1px solid #fde68a'
};

export const infoMessage = {
  marginTop: 8,
  padding: '8px 12px',
  backgroundColor: '#dbeafe',
  color: '#1e40af',
  borderRadius: 6,
  fontSize: 13,
  border: '1px solid #bfdbfe'
};

// Grid Layouts
export const grid2 = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 16
};

export const grid3 = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: 16
};

export const grid4 = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr 1fr',
  gap: 16
};

// Table Styles
export const table = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: typography.tableBody
};

export const th = {
  padding: '14px 16px',
  textAlign: 'left',
  borderBottom: `1px solid ${colors.gray200}`,
  fontWeight: 600,
  fontSize: typography.tableHeader,
  color: colors.gray700,
  backgroundColor: colors.gray50
};

export const td = {
  padding: '14px 16px',
  borderBottom: `1px solid ${colors.gray100}`,
  fontSize: typography.tableBody,
  color: colors.gray900
};

// Loading Spinner
export const loadingContainer = {
  textAlign: 'center',
  padding: 40,
  color: colors.gray500
};

// Badge
export const badge = {
  display: 'inline-block',
  padding: '4px 12px',
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600
};

export const badgeSuccess = {
  ...badge,
  backgroundColor: '#dcfce7',
  color: '#166534'
};

export const badgeDanger = {
  ...badge,
  backgroundColor: '#fee2e2',
  color: '#991b1b'
};

export const badgeWarning = {
  ...badge,
  backgroundColor: '#fef3c7',
  color: '#92400e'
};

export const badgeInfo = {
  ...badge,
  backgroundColor: '#dbeafe',
  color: '#1e40af'
};

// Pagination
export const paginationContainer = {
  marginTop: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 16,
  backgroundColor: colors.white,
  borderRadius: radii.lg,
  border: `1px solid ${colors.gray200}`,
  boxShadow: shadows.xs
};

export const paginationButton = {
  padding: '8px 12px',
  backgroundColor: colors.white,
  color: colors.gray700,
  border: `1px solid ${colors.gray300}`,
  borderRadius: radii.sm,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 500,
  transition: 'filter 0.15s, background-color 0.15s'
};

export const paginationButtonDisabled = {
  ...paginationButton,
  backgroundColor: colors.gray100,
  color: colors.gray500,
  cursor: 'not-allowed'
};

// Page Header
export const pageHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 24
};

export const pageTitle = {
  fontSize: typography.pageTitle,
  fontWeight: 700,
  color: colors.gray900
};

export const pageDescription = {
  fontSize: typography.pageSubtitle,
  color: colors.gray500,
  marginTop: 4
};

// Filter Badge
export const filterBadge = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  background: colors.primary50,
  border: `1px solid ${colors.primary}`,
  borderRadius: radii.sm,
  padding: '8px 12px',
  fontSize: 14,
  color: '#0c4a6e',
  lineHeight: 1.3
};

// Utility
export const spacer = {
  height: 24
};

export const divider = {
  height: 1,
  backgroundColor: colors.gray200,
  margin: '24px 0'
};

